import { css, html } from 'lit'
import { customElement, queryAssignedElements, state } from 'lit/decorators.js'
import { useEffect } from 'haunted'
import { stylesBootstrap } from '../styles/bootstrap';
import { EPProgressStepper } from './ep-progress-stepper';
import { EPMultiView } from './ep-multi-view';
import { LitHauntedElement } from '../mixins/lit-haunted-element';

@customElement('ep-multi-view-stepper')
export class EPMultiViewStepper extends LitHauntedElement {

  @queryAssignedElements({ slot: 'stepper', selector: 'ep-progress-stepper' })
  slottedStepper!: HTMLElement[];

  @queryAssignedElements({ slot: 'multiview', selector: 'ep-multi-view' })
  slottedMultiview!: HTMLElement[];

  get stepper(): EPProgressStepper | undefined {
    return this.slottedStepper.find(e => e instanceof EPProgressStepper) as EPProgressStepper;
  }

  get multiview(): EPMultiView | undefined {
    return this.slottedMultiview.find(e => e instanceof EPMultiView) as EPMultiView;
  }

  @state()
  instructionsStep: number = 0;

  private _previousStep() {
    this.instructionsStep = Math.min(this.stepper?.steps.length ?? 0, Math.max(-1, this.instructionsStep - 1));
  }
  private _nextStep() {
    this.instructionsStep = Math.min(this.stepper?.steps.length ?? 0, Math.max(-1, this.instructionsStep + 1));
  }
  get previousStepDisabled(): boolean {
    return this.instructionsStep <= 0;
  }
  get nextStepDisabled(): boolean {
    if (this.stepper === undefined) return true;
    return this.instructionsStep >= (this.stepper.steps.length - 1);
  }

  private _handleSlotChange() {
    this.requestUpdate('previousStepDisabled');
    this.requestUpdate('nextStepDisabled');
  }

  render() {
    useEffect(() => {
      if (this.stepper) this.stepper.activeIndex = this.instructionsStep;
      if (this.multiview) this.multiview.value = `${this.instructionsStep}`;
    }, [this.instructionsStep]);

    return html`
      <div class="vstack" style="gap: 25px">
        <slot name="stepper" @slotchange=${this._handleSlotChange}></slot>
        <div class="hstack">
          <sl-button variant="default" size="small" ?disabled=${this.previousStepDisabled} @click=${this._previousStep}>
            <sl-icon slot="prefix" name="chevron-left"></sl-icon>
            Previous
          </sl-button>
          <span class="spacer"></span>
          <sl-button variant="default" size="small" ?disabled=${this.nextStepDisabled} @click=${this._nextStep}>
            Next
            <sl-icon slot="suffix" name="chevron-right"></sl-icon>
          </sl-button>
        </div>
        <div class="vstack" style="gap: 10px">
          <slot name="multiview" @slotchange=${this._handleSlotChange}></slot>
        </div>
      </div>
    `
  }

  //#region Styles
  static styles = [stylesBootstrap, css`
    :host {
      display: block;
      --font-mono: ui-monospace, SFMono-Regular, SF Mono, Menlo, "Cascadia Mono", Consolas, Liberation Mono, monospace;
    }

    .vstack, .hstack {
      display: flex;
    }
    .hstack {
      flex-direction: row;
      align-items: center;
    }
    .vstack {
      flex-direction: column;
    }
    .grid {
      display: grid;
    }

    .spacer {
      display: inline-block;
      flex: 1;
    }
  `]
  //#endregion
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-multi-view-stepper': EPMultiViewStepper
  }
}
